import { isPrimaryButton } from "../gesture/pointer";
import type { Point } from "../types";
import { createFrameScheduler, type Tool, type ToolContext } from "./Tool";

/**
 * Two clicks measure a pixel distance; the chrome's measurement prompt
 * then asks for the real length and calibrates `pixelsPerMeter`.
 */
export const createMeasureTool = (): Tool => {
  let start: Point | null = null;
  let context: ToolContext | null = null;

  const frame = createFrameScheduler<Point>((client) => {
    if (!context || !start) return;
    const point = context.snap.snapPoint(context.toWorld(client));
    context.overlay.set({
      draft: {
        kind: "measure",
        from: start,
        to: point,
        pixelsPerMeter: context.store.getState().meta.pixelsPerMeter ?? null
      }
    });
  });

  const reset = () => {
    frame.cancel();
    start = null;
    context?.overlay.reset();
  };

  return {
    kind: "measure",
    cursor: "crosshair",
    isBusy: () => start !== null,
    onPointerDown(toolContext, event) {
      context = toolContext;
      if (!isPrimaryButton(event)) return;
      const point = toolContext.snap.snapPoint(
        toolContext.toWorld({ x: event.clientX, y: event.clientY })
      );
      if (!start) {
        start = point;
        return;
      }
      const pixelLength = Math.hypot(point.x - start.x, point.y - start.y);
      reset();
      toolContext.store.getState().setTool("select");
      if (pixelLength < 1) return;
      toolContext.store
        .getState()
        .setChrome({ pendingMeasurement: { pixelLength } });
    },
    onPointerMove(toolContext, event) {
      context = toolContext;
      if (!start) return;
      frame.schedule({ x: event.clientX, y: event.clientY });
    },
    onPointerUp() {},
    onKeyDown(toolContext, event) {
      if (event.key === "Escape" && start) {
        context = toolContext;
        reset();
        toolContext.store.getState().setTool("select");
        return true;
      }
      return false;
    },
    cancel(toolContext) {
      context = toolContext;
      reset();
    }
  };
};
